import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, ChevronUp, Pencil, Plus, RotateCcw, ShieldCheck, X } from 'lucide-react'
import type {
  CompanyIntelligenceContextItem,
  CompanyIntelligenceContextItemRequest,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import './CfoContextRulesManager.css'

type ContextRulesClient = Pick<
  ServerApiClient,
  | 'listCompanyIntelligenceContextItems'
  | 'createCompanyIntelligenceContextItem'
  | 'reviewCompanyIntelligenceContextItem'
  | 'supersedeCompanyIntelligenceContextItem'
>

type ContextScope = 'company' | 'workbook'

const ITEM_TYPES = [
  { id: 'management_priority', label: 'Management priority' },
  { id: 'analysis_preference', label: 'Analysis preference' },
  { id: 'presentation_preference', label: 'Presentation preference' },
  { id: 'known_operating_event', label: 'Known operating event' },
  { id: 'context_caution', label: 'Context caution' },
  { id: 'business_fact', label: 'Business fact' },
]

const itemTypeLabel = (value: string): string =>
  ITEM_TYPES.find((candidate) => candidate.id === value)?.label
  || value.replace(/[_:]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())

const contextItemText = (value: unknown): string => {
  if (typeof value === 'string') return value.trim()
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    for (const key of ['text', 'value', 'note', 'description']) {
      if (typeof record[key] === 'string' && record[key].trim()) return record[key].trim()
    }
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value ?? '')
  }
}

const isInactive = (item: CompanyIntelligenceContextItem): boolean =>
  item.confirmation_state === 'rejected'
  || item.confirmation_state === 'expired'
  || Boolean(item.expires_at && Date.parse(item.expires_at) <= Date.now())

const provenanceLabel = (item: CompanyIntelligenceContextItem): string => {
  if (item.confirmation_state === 'user_confirmed') return 'Confirmed by your team'
  const source = String(item.provenance?.source || item.authority_class || '').trim().toLowerCase()
  if (source.includes('web')) return 'Website-derived'
  if (source.includes('model') || source.includes('ai')) return 'AI-proposed'
  if (source.includes('user')) return 'Provided by your team'
  return 'Inferred'
}

const statusLabel = (item: CompanyIntelligenceContextItem): string => {
  if (isInactive(item)) return item.confirmation_state === 'rejected' ? 'Rejected' : 'Expired'
  if (item.confirmation_state === 'user_confirmed') return 'Confirmed'
  return 'Needs review'
}

export type CfoContextRulesManagerProps = {
  workbookId: string
  client: ContextRulesClient
}

export function CfoContextRulesManager({ workbookId, client }: CfoContextRulesManagerProps) {
  const [items, setItems] = useState<CompanyIntelligenceContextItem[]>([])
  const [scope, setScope] = useState<ContextScope>('company')
  const [itemType, setItemType] = useState(ITEM_TYPES[0].id)
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async (cancelled?: () => boolean) => {
    setLoading(true)
    setError(null)
    try {
      const response = await client.listCompanyIntelligenceContextItems(workbookId)
      if (!cancelled?.()) setItems(Array.isArray(response.items) ? response.items : [])
    } catch (cause) {
      if (!cancelled?.()) {
        setError(cause instanceof Error ? cause.message : 'Could not load AI context and rules.')
      }
    } finally {
      if (!cancelled?.()) setLoading(false)
    }
  }, [client, workbookId])

  useEffect(() => {
    let cancelled = false
    void load(() => cancelled)
    return () => { cancelled = true }
  }, [load])

  const visibleItems = useMemo(() => items.filter((item) => (
    item.scope === scope && (showHistory || !isInactive(item))
  )), [items, scope, showHistory])
  const historyCount = useMemo(() => items.filter((item) => item.scope === scope && isInactive(item)).length, [items, scope])

  const mutate = useCallback(async (key: string, operation: () => Promise<{ refresh_job_id?: string | null }>, success: string) => {
    setBusyId(key)
    setError(null)
    setNotice(null)
    try {
      const response = await operation()
      setNotice(response.refresh_job_id
        ? `${success} FuseGrid is validating a refreshed company-intelligence snapshot now.`
        : success)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The context update could not be saved.')
    } finally {
      setBusyId(null)
    }
  }, [load])

  const addItem = async () => {
    const text = draft.trim()
    if (!text) return
    const request: CompanyIntelligenceContextItemRequest = {
      scope,
      item_type: itemType,
      value: { text },
      confirmation_state: 'user_confirmed',
      provenance: { source: 'user', surface: 'context_rules_manager' },
    }
    await mutate('new', () => client.createCompanyIntelligenceContextItem(workbookId, request), 'Context saved.')
    setDraft('')
  }

  const saveEdit = async (item: CompanyIntelligenceContextItem) => {
    const text = editingText.trim()
    if (!text) return
    await mutate(
      item.context_item_id,
      () => client.supersedeCompanyIntelligenceContextItem(workbookId, item.context_item_id, {
        item_type: item.item_type,
        value: { text },
        expires_at: item.expires_at || null,
      }),
      'Context updated with its prior version preserved in history.',
    )
    setEditingId(null)
    setEditingText('')
  }

  return (
    <section className="cfo-context-rules" aria-labelledby="cfo-context-rules-title">
      <div className="cfo-context-rules-head">
        <div>
          <div className="cfo-context-rules-kicker"><ShieldCheck size={14} /> Shared AI intelligence</div>
          <h2 id="cfo-context-rules-title">Context &amp; Rules</h2>
          <p>
            Confirm what FuseGrid should know about the business and how it should analyze and present results.
            Website AI, Excel AI, and governed MCP reads use the same validated context; their allowed actions remain separate.
          </p>
        </div>
        <div className="cfo-context-rules-tabs" role="tablist" aria-label="Context scope">
          <button type="button" role="tab" aria-selected={scope === 'company'} onClick={() => setScope('company')}>Company</button>
          <button type="button" role="tab" aria-selected={scope === 'workbook'} onClick={() => setScope('workbook')}>Workbook</button>
        </div>
      </div>

      <div className="cfo-context-rules-scope-note">
        {scope === 'company'
          ? 'Company context is shared across eligible workbooks and AI surfaces for this company.'
          : 'Workbook rules apply only when this workbook is the active source or destination.'}
      </div>

      <div className="cfo-context-rules-add">
        <label>
          <span>Type</span>
          <select value={itemType} onChange={(event) => setItemType(event.target.value)}>
            {ITEM_TYPES.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>
        <label className="cfo-context-rules-add-text">
          <span>Instruction or fact</span>
          <textarea
            value={draft}
            rows={2}
            maxLength={2000}
            placeholder={scope === 'company'
              ? 'Example: Prioritize cash conversion and recurring-revenue retention in management commentary.'
              : 'Example: Present variances in whole dollars and preserve the existing board-report style.'}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
        <button type="button" className="cfo-context-rules-primary" disabled={!draft.trim() || busyId === 'new'} onClick={() => void addItem()}>
          <Plus size={15} /> {busyId === 'new' ? 'Saving…' : 'Add context'}
        </button>
      </div>

      {error ? <div className="cfo-context-rules-alert" role="alert">{error}</div> : null}
      {notice ? <div className="cfo-context-rules-notice" role="status">{notice}</div> : null}

      {loading ? (
        <div className="cfo-context-rules-state" role="status"><RotateCcw size={14} className="spin" /> Loading governed context…</div>
      ) : visibleItems.length ? (
        <div className="cfo-context-rules-list">
          {visibleItems.map((item) => {
            const inactive = isInactive(item)
            const editing = editingId === item.context_item_id
            const busy = busyId === item.context_item_id
            return (
              <article className={`cfo-context-rule ${inactive ? 'is-inactive' : ''}`} key={item.context_item_id} data-testid="context-rule">
                <div className="cfo-context-rule-main">
                  <div className="cfo-context-rule-meta">
                    <span>{itemTypeLabel(item.item_type)}</span>
                    <span>{provenanceLabel(item)}</span>
                    <span className={`status ${inactive ? 'muted' : item.confirmation_state === 'user_confirmed' ? 'confirmed' : 'review'}`}>{statusLabel(item)}</span>
                  </div>
                  {editing ? (
                    <textarea
                      aria-label="Edit context"
                      rows={3}
                      maxLength={2000}
                      value={editingText}
                      onChange={(event) => setEditingText(event.target.value)}
                    />
                  ) : <p>{contextItemText(item.value)}</p>}
                  <small>
                    {item.scope === 'company' ? 'All company AI surfaces' : 'This workbook only'}
                    {item.updated_at ? ` · updated ${item.updated_at.slice(0, 10)}` : ''}
                  </small>
                </div>
                {!inactive ? (
                  <div className="cfo-context-rule-actions">
                    {editing ? (
                      <>
                        <button type="button" className="good" disabled={busy || !editingText.trim()} onClick={() => void saveEdit(item)}><Check size={14} /> Save</button>
                        <button type="button" disabled={busy} onClick={() => { setEditingId(null); setEditingText('') }}><X size={14} /> Cancel</button>
                      </>
                    ) : (
                      <>
                        {item.confirmation_state !== 'user_confirmed' ? (
                          <>
                            <button type="button" className="good" disabled={busy} onClick={() => void mutate(item.context_item_id, () => client.reviewCompanyIntelligenceContextItem(workbookId, item.context_item_id, 'confirm'), 'Context confirmed.')}><Check size={14} /> Confirm</button>
                            <button type="button" className="danger" disabled={busy} onClick={() => void mutate(item.context_item_id, () => client.reviewCompanyIntelligenceContextItem(workbookId, item.context_item_id, 'reject'), 'Context rejected and removed from active intelligence.')}><X size={14} /> Reject</button>
                          </>
                        ) : null}
                        <button type="button" disabled={busy} onClick={() => { setEditingId(item.context_item_id); setEditingText(contextItemText(item.value)) }}><Pencil size={14} /> Edit</button>
                        <button type="button" disabled={busy} onClick={() => void mutate(item.context_item_id, () => client.reviewCompanyIntelligenceContextItem(workbookId, item.context_item_id, 'expire'), 'Context expired and removed from active intelligence.')}><X size={14} /> Expire</button>
                      </>
                    )}
                  </div>
                ) : null}
              </article>
            )
          })}
        </div>
      ) : (
        <div className="cfo-context-rules-state">No {scope} context has been saved yet.</div>
      )}

      {historyCount ? (
        <button type="button" className="cfo-context-rules-history" onClick={() => setShowHistory((value) => !value)}>
          {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {showHistory ? 'Hide' : 'Show'} {historyCount} prior {historyCount === 1 ? 'version' : 'versions'}
        </button>
      ) : null}
    </section>
  )
}

export default CfoContextRulesManager
